import React, { useEffect, useReducer, useRef } from 'react'
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import { useS4LContext } from '@s4l/react-context'
import { Form, Typography } from '@s4l/ds'
import RevolutCheckout, { type Locale as RevolutLocale } from '@revolut/checkout'

import styles from './styles.module.css'

export interface RevolutCardFormProps {
  orderToken: string
  onSuccess: () => void
  onError: (error: Error) => void
  disabled?: boolean
}

type FormState = {
  cardholderName: string
  status: 'idle' | 'ready' | 'submitting'
  cardValidationError: string | null
}

type FormAction =
  | { type: 'SET_CARDHOLDER_NAME'; payload: string }
  | { type: 'SET_READY' }
  | { type: 'SUBMIT' }
  | { type: 'SUBMIT_SUCCESS' }
  | { type: 'SUBMIT_ERROR' }
  | { type: 'SET_VALIDATION_ERROR'; payload: string | null }

const initialState: FormState = {
  cardholderName: '',
  status: 'idle',
  cardValidationError: null,
}

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_CARDHOLDER_NAME':
      return { ...state, cardholderName: action.payload }
    case 'SET_READY':
      return { ...state, status: 'ready' }
    case 'SUBMIT':
      return { ...state, status: 'submitting' }
    case 'SUBMIT_SUCCESS':
      return { ...state, status: 'ready' }
    case 'SUBMIT_ERROR':
      return { ...state, status: 'ready', cardValidationError: null }
    case 'SET_VALIDATION_ERROR':
      return { ...state, status: 'ready', cardValidationError: action.payload }
    default:
      return state
  }
}

const validateMinWords =
  ({ message }: { message: string }) =>
  (value: unknown): { isValid: true } | { isValid: false; message: string } => {
    const name = String(value).trim()
    const words = name.split(/\s+/).filter(Boolean)
    return words.length >= 2 ? { isValid: true } : { isValid: false, message }
  }

function RevolutCardForm({
  orderToken,
  onSuccess,
  onError,
  disabled = false,
}: Readonly<RevolutCardFormProps>) {
  const { domain, i18n } = useS4LContext()
  const cardFieldRef = useRef<HTMLDivElement>(null)
  const cardFieldInstanceRef = useRef<ReturnType<
    Awaited<ReturnType<typeof RevolutCheckout>>['createCardField']
  > | null>(null)

  const [state, dispatch] = useReducer(formReducer, initialState)
  const { cardholderName, status, cardValidationError } = state
  const isReady = status !== 'idle'
  const isSubmitting = status === 'submitting'

  useEffect(() => {
    if (!orderToken || !cardFieldRef.current) return undefined

    const init = async () => {
      try {
        if (!cardFieldRef.current) return

        const [languageError, language] = await domain.GetCurrentLanguageSharedUseCase.execute()
        const { createCardField } = await RevolutCheckout(
          orderToken,
          process.env.NODE_ENV === 'production' ? 'prod' : 'sandbox'
        )

        cardFieldInstanceRef.current = createCardField({
          target: cardFieldRef.current,
          locale: languageError || !language ? 'auto' : (language.value as RevolutLocale),
          styles: {
            default: {
              fontFamily: "'HK Grotesk', Arial, Helvetica, sans-serif", // We cannot use the global font-family because Revolut overrides it in their iframe
            },
          },
          classes: {
            default: styles.cardField,
            focused: styles.cardFieldFocused,
            invalid: styles.cardFieldInvalid,
          },
          onSuccess: () => {
            dispatch({ type: 'SUBMIT_SUCCESS' })
            onSuccess()
          },
          onError: error => {
            dispatch({ type: 'SUBMIT_ERROR' })
            onError(error)
          },
          onValidation: errors => {
            dispatch({ type: 'SET_VALIDATION_ERROR', payload: errors.at(-1)?.message ?? null })
          },
        })
        dispatch({ type: 'SET_READY' })
      } catch (error) {
        onError(error instanceof Error ? error : new Error('Failed to initialize Revolut'))
      }
    }

    init()

    return () => {
      cardFieldInstanceRef.current?.destroy()
      cardFieldInstanceRef.current = null
    }
  }, [domain, orderToken, i18n.locale, onSuccess, onError])

  const handleSubmit = () => {
    if (!cardFieldInstanceRef.current) return

    dispatch({ type: 'SUBMIT' })
    cardFieldInstanceRef.current.submit({
      name: cardholderName.trim(),
      savePaymentMethodFor: 'merchant',
    })
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className={styles.content} data-testid="revolut-card-field">
        <Form.Label>
          {i18n.formatMessage({ id: 'payment.cardholder_name.label' })}
          <Form.Input
            name="cardholderName"
            value={cardholderName}
            onChange={value =>
              dispatch({ type: 'SET_CARDHOLDER_NAME', payload: value ? String(value) : '' })
            }
            disabled={disabled}
            validations={[
              Form.Validations.required({
                message: i18n.formatMessage({ id: 'payment.cardholder_name.error' }),
              }),
              validateMinWords({
                message: i18n.formatMessage({ id: 'payment.cardholder_name.short' }),
              }),
            ]}
          />
        </Form.Label>

        <div>
          <div
            ref={cardFieldRef}
            className={styles.cardField}
            data-testid="revolut-widget-container"
          />
          {cardValidationError && (
            <Typography
              variant="caption"
              size="l"
              color="var(--color-error-500)"
              data-error-message="card-validation"
            >
              {cardValidationError}
            </Typography>
          )}
        </div>

        <Form.SubmitButton disabled={disabled || !isReady} isLoading={isSubmitting}>
          {i18n.formatMessage({ id: 'global.confirm-booking' })}
        </Form.SubmitButton>
      </div>
    </Form>
  )
}

RevolutCardForm.displayName = 'RevolutCardForm'

export default withErrorBoundary(RevolutCardForm)
